import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  TableCell,
  Switch,
  Box,
} from '@mui/material';
import SettingsInputSwitcher from '../../../../components/Settings/SettingsController/SettingsInputSwitcher';
import AccuroAppointmentReasonConfigurationModal from './AccuroAppointmentReasonConfigurationModal';
import AccuroProvidersConfigurationModal from './AccuroProvidersConfigurationModal';
import AccuroAppointmentStatusSyncButton from './AccuroAppointmentStatusSyncButton';
import { Controls } from '../../../../components/controls/Controls';
import AppointmentTypeConfigurationModal from '../../AppointmentTypeSetting/AppointmentTypeConfigurationModal';
import ResetSetting from '../../../ResetSettingButton';

export default function AccuroSettingCell({
  entity,
  setting,
  handleSave,
  refreshParent,
  setErrorMessage,
  setSuccessMessage,
  setValues,
  apiClient,
  accuroAppointmentReasons,
  accuroProviders,
  selectedPlans,
  accuroAppointmentTypes,
}) {
  const [isAppointmentReasonModalOpen, setIsAppointmentReasonModalOpen] = useState(false);
  const [isProviderModalOpen, setIsProviderModalOpen] = useState(false);
  const [isAppointmentTypeModalOpen, setIsAppointmentTypeModalOpen] = useState(false);

  const renderSettingContent = () => {
    switch (setting.name) {
      case 'accuroFilterByProviderIds':
        return (
          <Switch
            checked={setting.value === 'true'}
            onChange={(event) => {
              handleSave(setting, event.target.checked.toString());
            }}
            name="accuroFilterByProviderIds"
          />
        );
      case 'accuroAppointmentReasonIds':
        return (
          <>
            {accuroAppointmentReasons
              .filter((reason) => {
                if (setting.value === '') return false;
                return setting.value.includes(reason.reasonId.toString());
              })
              .map((reason) => (
                <div key={reason.reasonId}>{reason.name}</div>
              ))}
          </>
        );
      case 'accuroProviderIds':
        return (
          <>
            {accuroProviders
              .filter((provider) => {
                if (setting.value === '') return false;
                return setting.value.split(',').includes(provider.providerId.toString());
              })
              .map((provider) => (
                <div key={provider.providerId}>
                  {`${provider.title} ${provider.firstName} ${provider.lastName}`}
                </div>
              ))}
          </>
        );

      case 'accuroAppointmentType':
        try {
          const appointmentTypes = setting.value ? JSON.parse(setting.value) : [];
          return (
            <>
              {appointmentTypes.map((appointmentType) => {
                const selectedPlan = appointmentType.intakePlanId
                  ? selectedPlans.find((plan) => plan.id === appointmentType.intakePlanId)
                  : null;
                return (
                  <div key={appointmentType.id}>
                    <b>{`${appointmentType.name}`}</b>
                    {' : '}
                    {selectedPlan ? `(${selectedPlan.referenceCode} : ${selectedPlan.moduleName})` : 'Please configure intake plan'}
                  </div>
                );
              })}
            </>
          );
        } catch {
          return 'Not configured';
        }

      case 'accuroSendDays':
        return (
          <SettingsInputSwitcher
            type="toggle"
            setting={setting}
            refreshParent={refreshParent}
            setErrorMessage={setErrorMessage}
            setSuccessMessage={setSuccessMessage}
            options={[
              { name: 'Mon', value: 1 },
              { name: 'Tues', value: 2 },
              { name: 'Wed', value: 3 },
              { name: 'Thurs', value: 4 },
              { name: 'Fri', value: 5 },
              { name: 'Sat', value: 6 },
              { name: 'Sun', value: 7 },
            ]}
            defaultOption={{ name: 'All', value: '' }}
            contextType="site"
          />
        );

      case 'accuroSentSMSStatus':
      case 'accuroNotSentSMSStatus':
      case 'accuroIntakeCompletedStatus':
        return setting.value;

      default:
        return (
          <SettingsInputSwitcher
            type="text"
            setting={setting}
            setValues={setValues}
            refreshParent={refreshParent}
            setSuccessMessage={setSuccessMessage}
            setErrorMessage={setErrorMessage}
            contextType="site"
          />
        );
    }
  };

  const renderActionCell = () => {
    switch (setting.name) {
      case 'accuroAppointmentReasonIds':
        return (
          <>
            <Controls.Button
              onClick={() => setIsAppointmentReasonModalOpen(true)}
              text="Edit"
            />
            <AccuroAppointmentReasonConfigurationModal
              isOpen={isAppointmentReasonModalOpen}
              handleClose={() => setIsAppointmentReasonModalOpen(false)}
              handleSave={handleSave}
              setting={setting}
              appointmentReasons={accuroAppointmentReasons}
            />
          </>
        );
      case 'accuroProviderIds':
        return (
          <>
            <Controls.Button
              onClick={() => setIsProviderModalOpen(true)}
              text="Edit"
            />
            <AccuroProvidersConfigurationModal
              isOpen={isProviderModalOpen}
              handleClose={() => setIsProviderModalOpen(false)}
              handleSave={handleSave}
              providers={accuroProviders}
              setting={setting}
            />
          </>
        );
      case 'accuroAppointmentType':
        return (
          <>
            <Controls.Button
              onClick={() => setIsAppointmentTypeModalOpen(true)}
              text="Edit"
            />
            <AppointmentTypeConfigurationModal
              isOpen={isAppointmentTypeModalOpen}
              handleClose={() => setIsAppointmentTypeModalOpen(false)}
              setting={setting}
              selectedPlans={selectedPlans}
              handleSave={handleSave}
              appointmentTypes={accuroAppointmentTypes}
            />
          </>
        );
      case 'accuroSentSMSStatus':
      case 'accuroNotSentSMSStatus':
      case 'accuroIntakeCompletedStatus':
        return (
          <AccuroAppointmentStatusSyncButton
            setting={setting}
            apiClient={apiClient}
            handleSave={handleSave}
            setErrorMessage={setErrorMessage}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <TableCell>{renderSettingContent()}</TableCell>
      <TableCell>
        <Box display="flex" gap={1}>
          {renderActionCell()}
          <ResetSetting
            entity={entity}
            type="site"
            setting={setting}
            refreshParent={refreshParent}
            setErrorMessage={setErrorMessage}
            setSuccessMessage={setSuccessMessage}
            minimize
          />
        </Box>
      </TableCell>
    </>
  );
}

// Define PropTypes for the component
const settingShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
});

const providerShape = PropTypes.shape({
  providerId: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
});

const appointmentReasonShape = PropTypes.shape({
  reasonId: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
});

const appointmentTypeShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  intakePlanId: PropTypes.number,
});

const planShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  referenceCode: PropTypes.string.isRequired,
  moduleName: PropTypes.string.isRequired,
});

AccuroSettingCell.propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    settings: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      category: PropTypes.string,
      value: PropTypes.string,
    })),
  }).isRequired,
  setting: settingShape.isRequired,
  handleSave: PropTypes.func.isRequired,
  refreshParent: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setSuccessMessage: PropTypes.func.isRequired,
  setValues: PropTypes.func.isRequired,
  apiClient: PropTypes.shape({
    getAccuroProviders: PropTypes.func.isRequired,
    getAccuroAppointmentReasons: PropTypes.func.isRequired,
    getAccuroAppointmentTypes: PropTypes.func.isRequired,
    getIntakePlans: PropTypes.func.isRequired,
  }).isRequired,
  accuroAppointmentReasons: PropTypes.arrayOf(appointmentReasonShape).isRequired,
  accuroProviders: PropTypes.arrayOf(providerShape).isRequired,
  selectedPlans: PropTypes.arrayOf(planShape).isRequired,
  accuroAppointmentTypes: PropTypes.arrayOf(appointmentTypeShape).isRequired,
};
